import React, { useState } from 'react';
import { Alert, Box, Button } from '@mui/material';
import { CaseworkerAbsenceModel } from '../../../../api-service/clients';
import { DatePicker, DatePickerValue } from '../../../../components/date-picker';
import { TextField } from '../../../../components/text-field/text-field';
import { toStartOfDay } from '../../../../utilities/date-helper';
import { absenceDay } from './absence-day';
import { AbsenceStatusBarPreview } from './absence-status-bar-preview';

const maximumTextLength = 500;

interface IAbsencePeriod {
  startDate: Date;
  endDate?: Date;
}

export interface IAbsenceForm {
  startDate: DatePickerValue;
  endDate: DatePickerValue;
  text: string;
}

export type AbsenceFormErrors = Partial<Record<'startDate' | 'endDate', string>>;

function periodsOverlap(first: IAbsencePeriod, second: IAbsencePeriod): boolean {
  const firstEnd = first.endDate?.getTime() ?? Infinity;
  const secondEnd = second.endDate?.getTime() ?? Infinity;
  return first.startDate.getTime() <= secondEnd && second.startDate.getTime() <= firstEnd;
}

function overlapsAnyOtherAbsence(startDate: Date, endDate: Date | undefined, otherAbsences: CaseworkerAbsenceModel[]): boolean {
  return otherAbsences.some(other =>
    periodsOverlap({ startDate, endDate }, { startDate: absenceDay(other.startDate), endDate: other.endDate ? absenceDay(other.endDate) : undefined })
  );
}

export function validateAbsenceForm(form: IAbsenceForm, otherAbsences: CaseworkerAbsenceModel[] = []): AbsenceFormErrors {
  const errors: AbsenceFormErrors = {};
  const startDate = toStartOfDay(form.startDate);
  const endDate = toStartOfDay(form.endDate);

  if (!startDate) {
    errors.startDate = 'Startdato skal udfyldes.';
  }
  if (startDate && endDate && endDate < startDate) {
    errors.endDate = 'Slutdatoen kan ikke ligge før startdatoen.';
  }
  if (startDate && !errors.endDate && overlapsAnyOtherAbsence(startDate, endDate, otherAbsences)) {
    errors.startDate = 'Perioden overlapper et andet fravær.';
  }
  return errors;
}

export interface ICaseworkerAbsenceFormProps {
  absenceToEdit?: CaseworkerAbsenceModel;
  absences: CaseworkerAbsenceModel[];
  caseworkerName?: string;
  isSaving: boolean;
  saveError?: string;
  onSave: (form: IAbsenceForm) => void;
  onCancel: () => void;
}

export function CaseworkerAbsenceForm(props: ICaseworkerAbsenceFormProps) {
  const { absenceToEdit, absences, caseworkerName, isSaving, saveError, onSave, onCancel } = props;
  const [form, setForm] = useState<IAbsenceForm>(() => ({
    startDate: absenceToEdit && absenceDay(absenceToEdit.startDate),
    endDate: absenceToEdit?.endDate ? absenceDay(absenceToEdit.endDate) : undefined,
    text: absenceToEdit?.text ?? '',
  }));
  const [errors, setErrors] = useState<AbsenceFormErrors>({});
  const [isStartDateInvalid, setIsStartDateInvalid] = useState(false);
  const [isEndDateInvalid, setIsEndDateInvalid] = useState(false);

  function updateForm(changes: Partial<IAbsenceForm>) {
    setForm(current => ({ ...current, ...changes }));
  }

  function handleStartDateValidation(failureCause: string | undefined) {
    setIsStartDateInvalid(!!failureCause);
  }

  function handleEndDateValidation(failureCause: string | undefined) {
    setIsEndDateInvalid(!!failureCause);
  }

  function handleSave() {
    const otherAbsences = isEndDateInvalid ? [] : absences.filter(absence => absence.absenceId !== absenceToEdit?.absenceId);
    const foundErrors = validateAbsenceForm(form, otherAbsences);
    if (isStartDateInvalid) {
      delete foundErrors.startDate;
    }
    if (isEndDateInvalid) {
      delete foundErrors.endDate;
    }
    setErrors(foundErrors);
    if (Object.keys(foundErrors).length === 0 && !isStartDateInvalid && !isEndDateInvalid) {
      onSave(form);
    }
  }

  return (
    <Box display="flex" flexDirection="column" gap={2} width="100%">
      <Box display="flex" flexWrap="wrap" gap={2}>
        <DatePicker
          label="Startdato"
          value={form.startDate}
          required
          variant="outlined"
          error={!!errors.startDate}
          errorText={errors.startDate}
          onChange={startDate => updateForm({ startDate })}
          onInternalValidation={handleStartDateValidation}
        />
        <DatePicker
          label="Slutdato"
          value={form.endDate}
          variant="outlined"
          error={!!errors.endDate}
          errorText={errors.endDate}
          onChange={endDate => updateForm({ endDate })}
          onInternalValidation={handleEndDateValidation}
        />
      </Box>
      <TextField
        label="Fraværstekst"
        value={form.text}
        multiline
        minRows={4}
        maxRows={10}
        variant="outlined"
        maxLength={maximumTextLength}
        helperText={`${maximumTextLength - form.text.length} tegn tilbage`}
        onChange={event => updateForm({ text: event.target.value })}
      />
      {saveError && <Alert severity="error">{saveError}</Alert>}
      <AbsenceStatusBarPreview caseworkerName={caseworkerName} startDate={form.startDate} endDate={form.endDate} text={form.text} />
      <Box display="flex" gap={1}>
        <Button type="button" variant="contained" disabled={isSaving} onClick={handleSave}>
          Gem
        </Button>
        <Button type="button" variant="outlined" disabled={isSaving} onClick={onCancel}>
          Annullér
        </Button>
      </Box>
    </Box>
  );
}
